import React, { useState, useEffect } from 'react';
import Card from '../components/common/Card';
import { Search, ChevronLeft, ChevronRight, Users, Filter, X, Eye } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

const AlumniDirectory = () => {
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalAlumni, setTotalAlumni] = useState(0);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchDepartments();
    fetchAlumni();
  }, [currentPage, itemsPerPage, searchTerm, selectedDepartment]);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchAlumni = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        department: selectedDepartment
      };
      
      const response = await api.get('/alumni/directory', { params });
      
      if (response.data.success) {
        setAlumni(response.data.alumni);
        setTotalAlumni(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching alumni directory:', error);
      toast.error('Failed to load alumni directory');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleDepartmentFilter = (deptId) => {
    setSelectedDepartment(deptId === selectedDepartment ? '' : deptId);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDepartment('');
    setCurrentPage(1);
    setShowFilters(false);
  };

  const handleViewDetails = (alumnus) => {
    setSelectedAlumni(alumnus);
    setShowDetailsModal(true);
  };

  const totalPages = Math.ceil(totalAlumni / itemsPerPage);

  const getCourseDisplay = (alumnus) => {
    if (!alumnus.course_name) return 'N/A';
    let display = alumnus.course_name;
    if (alumnus.exam_body_code === 'TVET-CDACC') {
      if (alumnus.level_number === 6) {
        display += ' (Level 6)';
      } else if (alumnus.level_number === 5) {
        display += ' (Level 5)';
      } else if (alumnus.level_number === 4) {
        display += ' (Level 4)';
      } else if (alumnus.level_number === 3) {
        display += ' (Level 3)';
      } else if (alumnus.level_number) {
        display += ` (Level ${alumnus.level_number})`;
      }
    } else if (alumnus.exam_body_code === 'KNEC') {
      if (alumnus.level_name === 'Diploma') {
        display += ' (Diploma)';
      } else if (alumnus.level_name === 'Certificate') {
        display += ' (Certificate)';
      } else if (alumnus.level_name === 'Artisan') {
        display += ' (Artisan)';
      } else if (alumnus.level_name) {
        display += ` (${alumnus.level_name})`;
      }
    } else if (alumnus.exam_body_code === 'NITA') {
      if (alumnus.level_name === 'Grade I') {
        display += ' (Grade I)';
      } else if (alumnus.level_name === 'Grade II') {
        display += ' (Grade II)';
      } else if (alumnus.level_name === 'Grade III') {
        display += ' (Grade III)';
      } else if (alumnus.level_name) {
        display += ` (${alumnus.level_name})`;
      }
    } else if (alumnus.level_name) {
      display += ` (${alumnus.level_name})`;
    } else if (alumnus.level_number) {
      display += ` (Level ${alumnus.level_number})`;
    }
    return display;
  };

  const getInitials = (name) => {
    return name ? name.charAt(0).toUpperCase() : '?';
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Alumni Directory</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">Connect with fellow Katine TTI alumni</p>
      </div>

      {/* Stats Card */}
      <div className="bg-gradient-to-r from-primary to-primary-dark rounded-lg shadow mb-6">
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-xs sm:text-sm">Total Alumni Members</p>
              <p className="text-3xl sm:text-4xl font-bold text-white mt-1">{totalAlumni}</p>
            </div>
            <div className="bg-white/20 rounded-full p-3 sm:p-4">
              <Users className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="p-3 sm:p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
            <input
              type="text"
              placeholder="Search by name, admission number, or course..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 sm:px-4 py-2 text-sm sm:text-base border rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">Filters</span>
            </button>
            {(searchTerm || selectedDepartment) && (
              <button
                onClick={clearFilters}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline">Clear</span>
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t">
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Department</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleDepartmentFilter('')}
                className={`px-3 py-1 rounded-full text-xs sm:text-sm ${selectedDepartment === '' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                All
              </button>
              {departments.map(dept => (
                <button
                  key={dept.id}
                  onClick={() => handleDepartmentFilter(dept.id)}
                  className={`px-3 py-1 rounded-full text-xs sm:text-sm ${selectedDepartment === dept.id ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {dept.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Items Per Page - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Show:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="border rounded-md px-2 py-1 text-sm"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-sm text-gray-600">entries</span>
        </div>
        <div className="text-sm text-gray-500">
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalAlumni)} of {totalAlumni} alumni
        </div>
      </div>

      {/* Alumni Table - Horizontal Scroll for Mobile */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="min-w-[1000px] md:min-w-full w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Photo</th>
                <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Full Name</th>
                <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No.</th>
                <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Membership No.</th>
                <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                <th className="px-2 sm:px-3 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="11" className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-gray-500">Loading alumni...</p>
                  </td>
                </tr>
              ) : alumni.length === 0 ? (
                <tr>
                  <td colSpan="11" className="px-6 py-12 text-center text-gray-500">No alumni found</td>
                </tr>
              ) : (
                alumni.map((alumnus, index) => (
                  <tr key={alumnus.id} className="hover:bg-gray-50">
                    <td className="px-2 sm:px-3 py-2 sm:py-3 text-sm text-gray-500">{((currentPage - 1) * itemsPerPage) + index + 1}</td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3">
                      {alumnus.profile_image ? (
                        <img src={alumnus.profile_image} alt={alumnus.full_name} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-xs sm:text-sm font-bold text-primary">{getInitials(alumnus.full_name)}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3 font-medium text-gray-900 whitespace-nowrap">{alumnus.full_name}</td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3"><code className="text-xs bg-gray-100 px-1 sm:px-2 py-1 rounded whitespace-nowrap">{alumnus.admin_number}</code></td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3"><code className="text-xs font-bold text-primary bg-primary/10 px-1 sm:px-2 py-1 rounded whitespace-nowrap">{alumnus.alumni_membership_number || 'Pending...'}</code></td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3 text-sm text-gray-600 whitespace-nowrap">{alumnus.email || 'N/A'}</td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3 text-sm text-gray-600 whitespace-nowrap">{alumnus.telephone || 'N/A'}</td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3"><span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 whitespace-nowrap">{alumnus.department_name || 'N/A'}</span></td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3 text-sm text-gray-700 whitespace-nowrap">{getCourseDisplay(alumnus)}</td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3"><span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 whitespace-nowrap">{alumnus.grade || 'N/A'}</span></td>
                    <td className="px-2 sm:px-3 py-2 sm:py-3">
                      <button onClick={() => handleViewDetails(alumnus)} className="text-primary hover:text-primary-dark">
                        <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination - Mobile Responsive */}
        {!loading && totalPages > 1 && (
          <div className="px-3 sm:px-6 py-3 sm:py-4 bg-gray-50 border-t flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="text-xs sm:text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-2 sm:px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-2 sm:px-3 py-1 text-sm">Page {currentPage} of {totalPages}</span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-2 sm:px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal - Mobile Responsive */}
      {showDetailsModal && selectedAlumni && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">Alumni Details</h2>
              <button onClick={() => setShowDetailsModal(false)} className="text-gray-500 text-2xl">×</button>
            </div>
            <div className="p-4 sm:p-6">
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                {selectedAlumni.profile_image ? (
                  <img src={selectedAlumni.profile_image} alt={selectedAlumni.full_name} className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-2xl sm:text-3xl font-bold text-primary">{getInitials(selectedAlumni.full_name)}</span>
                  </div>
                )}
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{selectedAlumni.full_name}</h3>
                  <p className="text-sm text-gray-500">Alumni Member</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div><label className="text-xs text-gray-500 uppercase">Admission Number</label><p className="font-mono mt-1 text-sm">{selectedAlumni.admin_number}</p></div>
                <div><label className="text-xs text-gray-500 uppercase">Membership Number</label><p className="text-primary font-bold mt-1 text-sm">{selectedAlumni.alumni_membership_number || 'Pending...'}</p></div>
                <div><label className="text-xs text-gray-500 uppercase">Email</label><p className="mt-1 text-sm break-all">{selectedAlumni.email || 'N/A'}</p></div>
                <div><label className="text-xs text-gray-500 uppercase">Phone</label><p className="mt-1 text-sm">{selectedAlumni.telephone || 'N/A'}</p></div>
                <div className="sm:col-span-2"><label className="text-xs text-gray-500 uppercase">Department</label><p className="mt-1 text-sm">{selectedAlumni.department_name || 'N/A'}</p></div>
                <div className="sm:col-span-2"><label className="text-xs text-gray-500 uppercase">Course</label><p className="mt-1 text-sm">{getCourseDisplay(selectedAlumni)}</p></div>
                <div><label className="text-xs text-gray-500 uppercase">Grade</label><p className="mt-1 text-sm">{selectedAlumni.grade || 'N/A'}</p></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlumniDirectory;
